import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SiteNav from "components/Redesign/SiteNav.js";
import TrachtWidget from "components/Redesign/TrachtWidget.js";
import SiteFooter from "components/Redesign/SiteFooter.js";
import SignUpEmail from "views/examples/SignUpEmail.js";
import b2bImg from "assets/img/product-photos/b2b-overview.jpg";
import teamImg from "assets/img/team/team.jpg";

const PRODUCT_CARDS = [
  { key: "fruehlingsblute", name: "Frühlingsblüte", theme: "card-sage", location: "Dachlissen · Zürich", seasonKey: "Season spring", elevation: "470 M.ü.M." },
  { key: "wald", name: "Wald", theme: "card-wald", location: "Dachlissen · Zürich", seasonKey: "Season summer", elevation: "470 M.ü.M." },
  { key: "primavera", name: "Primavera", theme: "card-primavera", location: "Malcantone · Ticino", seasonKey: "Season spring", elevation: "300 – 630 M.ü.M." },
  { key: "estate", name: "Estate", theme: "card-estate", location: "Malcantone · Ticino", seasonKey: "Season summer", elevation: "300 – 630 M.ü.M." },
  { key: "alpenwiese", name: "Alpenwiese", theme: "card-alpenwiese", location: "Falera · Graubünden", seasonKey: "Season summer", elevation: "1450 M.ü.M." },
];

function ProductCard({ card }) {
  const { t } = useTranslation();
  return (
    <Link to="/unser-honig" className={"pcard " + card.theme}>
      <div className="pcard-inner">
        <div className="pcard-meta-top">
          <div>
            <div className="pcard-location">{card.location}</div>
          </div>
          <div className="pcard-size">{t("home." + card.seasonKey)}</div>
        </div>
        <div className="pcard-body">
          <p className="pcard-desc">{t("home.Card " + card.key + " desc")}</p>
        </div>
        <div className="pcard-bottom">
          <span className="pcard-elev">{card.elevation}</span>
        </div>
      </div>
      <div className="pcard-name">{card.name}</div>
    </Link>
  );
}

function Home() {
  const { t } = useTranslation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="rd">
      <SiteNav />

      {/* Hero */}
      <section>
        <div className="hero-banner">
          <img
            src={require("assets/img/hero/home-hero.jpg")}
            alt="Nahaufnahme zweier Honigbienen"
            className="hero-banner-img"
          />
        </div>

        <div className="hero-body">
          <div className="hero-body-inner">
            <div className="hero-left">
              <div className="hero-eyebrow">{t("home.Hero eyebrow")}</div>
              <h1 className="hero-h1">
                {t("home.Hero H1 pre")}<br />
                <em>{t("home.Hero H1 em")}</em>
                {/* Kein festes Leerzeichen: Sprachen, deren Satzbau das
                    betonte Wort ans Ende stellt, lassen "post" leer — sonst
                    stuende dort ein freischwebendes Satzzeichen. */}
                {t("home.Hero H1 post") ? " " + t("home.Hero H1 post") : null}
              </h1>
              <p className="hero-desc pre-line">{t("home.Hero desc")}</p>
            </div>
            <div className="hero-ctas">
              <Link to="/unser-honig" className="cta-primary">{t("home.Hero CTA shop")} <span className="arr">→</span></Link>
              <Link to="/ueber-uns/unsere-geschichte" className="cta-secondary">{t("home.Hero CTA story")} <span className="arr">→</span></Link>
            </div>
          </div>
        </div>

        <div className="hero-stats">
          <div className="hero-stats-inner">
            <div className="stat"><span className="stat-num">29</span><span className="stat-label">{t("home.Stat hives")}</span></div>
            <div className="stat"><span className="stat-num">5</span><span className="stat-label">{t("home.Stat varieties")}</span></div>
            <div className="stat"><span className="stat-num">3</span><span className="stat-label">{t("home.Stat locations")}</span></div>
            <div className="stat"><span className="stat-num">20+</span><span className="stat-label">{t("home.Stat years")}</span></div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="philosophy">
        <div className="wrap">
          <div className="phil-inner">
            <div className="phil-left">
              <div>
                <div className="phil-section-no">{t("home.Phil label")}</div>
                <h2 className="phil-h2 pre-line">{t("home.Phil title")}</h2>
              </div>
              <div className="phil-photo">
                <img src={teamImg} alt="Die Familie Bühler in Imkeranzügen bei den Bienenstöcken" />
              </div>
              <figure className="phil-quote">
                <blockquote>{t("home.Phil quote")}</blockquote>
                <figcaption>{t("home.Phil quote author")}</figcaption>
              </figure>
            </div>
            <div className="phil-right">
              <div className="phil-body-text">
                <p>{t("home.Phil body p1")}</p>
                <p>{t("home.Phil body p2")}</p>
              </div>
              <div className="phil-cta">
                <Link to="/ueber-uns" className="cta-secondary-block">{t("home.Phil CTA")} <span className="arr">→</span></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sortiment */}
      <section className="sortiment">
        <div className="wrap">
          <div className="sortiment-head">
            <div>
              <div className="rd-section-no">{t("home.Sortiment label")}</div>
              <h2 className="sortiment-h2">
                {t("home.Sortiment title")}<br />
                <span className="sortiment-h2-accent">{t("home.Sortiment title accent")}</span>
              </h2>
            </div>
          </div>

          <div className="feature-row">
            <span className="feat-pill">{t("home.Pill 1")}</span>
            <span className="feat-pill">{t("home.Pill 2")}</span>
            <span className="feat-pill">{t("home.Pill 3")}</span>
            <span className="feat-pill">{t("home.Pill 4")}</span>
          </div>

          <div className="product-grid">
            {PRODUCT_CARDS.slice(0, 3).map(card => <ProductCard key={card.key} card={card} />)}
          </div>
          <div className="product-grid-2">
            {PRODUCT_CARDS.slice(3).map(card => <ProductCard key={card.key} card={card} />)}
            <div className="pcard-cta-box">
              <div className="pcard-cta-label">{t("home.Sortiment CTA label")}</div>
              <Link to="/unser-honig" className="pcard-cta-link">{t("home.Sortiment CTA")} <span className="arr">→</span></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Live aus dem Bienenstock */}
      <section className="bienen">
        <div className="wrap">
          <div className="bienen-inner">
            <div className="bienen-left">
              <div className="rd-section-no">{t("home.Bienen label")}</div>
              <h2 className="pre-line">{t("home.Bienen title")}</h2>
              <p>{t("home.Bienen text")}</p>
              <Link to="/live" className="btn-gold">{t("home.Bienen CTA")} <span className="arr">→</span></Link>
            </div>
            <div>
              <TrachtWidget />
            </div>
          </div>
        </div>
      </section>

      {/* B2B */}
      <section className="b2b">
        <div className="wrap">
          <div className="b2b-inner">
            <div className="b2b-visual">
              <img src={b2bImg} alt="Sortiment aller fünf Honigsorten für Firmengeschenke" className="b2b-visual-img" />
              <div className="b2b-visual-label">{t("home.B2B visual label")}</div>
            </div>
            <div className="b2b-text">
              <div className="rd-section-no b2b-no">{t("home.B2B label")}</div>
              <h2>{t("home.B2B title")}</h2>
              <p>{t("home.B2B text")}</p>
              <div>
                <Link to="/fuer-unternehmen" className="btn-forest">{t("home.B2B CTA")}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter — bestehendes Formular (EmailJS + reCAPTCHA) eingebettet */}
      <section className="newsletter">
        <div className="wrap">
          <div className="newsletter-grid">
            <div className="newsletter-text">
              <div className="rd-section-no">{t("home.Newsletter label")}</div>
              <h2>{t("home.Newsletter title")}</h2>
              <p className="newsletter-lede">{t("home.Newsletter text")}</p>
            </div>
            <div className="newsletter-embed">
              <SignUpEmail />
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export default Home;
